import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// ============================================
// API CONFIG
// ============================================
const API_BASE_URL = 'https://test-bot-lake-six.vercel.app';

// ============================================
// SESSION STORAGE MANAGER
// ============================================
const STORAGE_KEY = 'websty_chat_session';
const SESSION_EXPIRY_DAYS = 7; // Auto-delete conversations older than 7 days

class ChatSessionManager {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
  }

  // Get or create a unique session ID
  getOrCreateSessionId() {
    let sessionId = localStorage.getItem('websty_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('websty_session_id', sessionId);
    }
    return sessionId;
  }

  // Load conversation history from localStorage
  loadConversation() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      const data = JSON.parse(stored);
      
      // Check if session expired
      if (data.expiry && Date.now() > data.expiry) {
        this.clearConversation();
        return [];
      }

      return data.messages || [];
    } catch (error) {
      console.error('Error loading conversation:', error);
      return [];
    }
  }

  // Save conversation to localStorage
  saveConversation(messages) {
    const data = {
      messages: messages,
      sessionId: this.sessionId,
      lastUpdated: Date.now(),
      expiry: Date.now() + (SESSION_EXPIRY_DAYS * 24 * 60 * 60 * 1000) // 7 days from now
    };
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving conversation:', error);
      // If storage is full, try to clean up old data
      if (error.name === 'QuotaExceededError') {
        this.cleanupOldSessions();
        // Try again
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (e) {
          console.error('Storage still full after cleanup:', e);
        }
      }
    }
  }

  // Clear conversation (End Chat button)
  clearConversation() {
    localStorage.removeItem(STORAGE_KEY);
    // Optionally also clear session ID to start fresh
    // localStorage.removeItem('websty_session_id');
  }

  // Cleanup old sessions (if storage gets full)
  cleanupOldSessions() {
    // Remove expired sessions
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('websty_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data.expiry && Date.now() > data.expiry) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          // Ignore non-JSON items
        }
      }
    });
  }

  // Get session ID for API calls
  getSessionId() {
    return this.sessionId;
  }
}

// ============================================
// WIDGET POSITION CONFIGURATION
// ============================================
// All positional variables in one place for easy tweaking
const WIDGET_CONFIG = {
  // Button dimensions
  BUTTON_SIZE: 64,           // Button size in pixels
  
  // Iframe dimensions (when widget is closed)
  IFRAME_SIZE: 96,            // Iframe size in pixels (larger than button)
  
  // Widget dimensions (when widget is open)
  WIDGET_WIDTH: 380,          // Widget width when open
  WIDGET_HEIGHT: 600,          // Widget height when open
  
  // Position offsets
  POSITION_BOTTOM: 24,        // Distance from bottom (px) - for button and widget
  POSITION_RIGHT: 24,          // Distance from right (px) - for button and widget
  IFRAME_BOTTOM: 0,           // Iframe distance from bottom (px)
  IFRAME_RIGHT: 0,            // Iframe distance from right (px)
};

// ============================================
// PURE HELPER FUNCTIONS (outside component to avoid hook dependency warnings)
// ============================================

const extractEmail = (text) => {
  if (!text) return null;
  const match = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return match ? match[0] : null;
};

const extractPhone = (text) => {
  if (!text) return null;
  const match = text.match(/(\+?\d[\d\s-]{6,}\d)/);
  return match ? match[0].trim() : null;
};

const extractContactInfo = (fullConversation) => {
  let email = null;
  let phone = null;
  for (const msg of fullConversation) {
    if (msg.role === 'user' && msg.content) {
      const foundEmail = extractEmail(msg.content);
      const foundPhone = extractPhone(msg.content);
      if (foundEmail && !email) email = foundEmail;
      if (foundPhone && !phone) phone = foundPhone;
      if (email && phone) break;
    }
  }
  return { email, phone };
};

function App() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState('en'); // 'en' | 'lt' | 'ru' | 'pl'
  const [showLanguageMenu, setShowLanguageMenu] = useState(true); // Show language menu initially
  const [showDropdown, setShowDropdown] = useState(false); // Track dropdown visibility
  const [leadSent, setLeadSent] = useState(false); // Avoid sending multiple leads per session

  // Create session manager instance (using useRef to persist across renders)
  const sessionManagerRef = useRef(new ChatSessionManager());

  // Refs to avoid stale closures inside the inactivity timer callback
  const messagesRef = useRef([]);
  const leadSentRef = useRef(false);
  const inactivityTimerRef = useRef(null);
  const INACTIVITY_MS = 15 * 60 * 1000; // 15 minutes

  const WELCOME_BY_LANG = {
    en: '👋 Hi! How can I help you today?',
    lt: '👋 Sveiki! Kaip galiu jums padėti?',
    ru: '👋 Привет! Чем я могу помочь?',
    pl: '👋 Cześć! Jak mogę pomóc?',
  };
  const HEADER_BY_LANG = {
    en: 'Chat with us',
    lt: 'Susisiekite su mumis',
    ru: 'Свяжитесь с нами',
    pl: 'Porozmawiaj z nami',
  };
  const END_CHAT_BY_LANG = {
    en: 'End Chat',
    lt: 'Baigti pokalbį',
    ru: 'Завершить чат',
    pl: 'Zakończ czat',
  };
  const CHANGE_LANGUAGE_BY_LANG = {
    en: 'Change Language',
    lt: 'Keisti kalbą',
    ru: 'Изменить язык',
    pl: 'Zmień język',
  };
  const END_CHAT_CONFIRM_BY_LANG = {
    en: 'Are you sure you want to end this chat? The conversation will be cleared.',
    lt: 'Ar tikrai norite baigti šį pokalbį? Pokalbis bus ištrintas.',
    ru: 'Вы уверены, что хотите завершить этот чат? Разговор будет очищен.',
    pl: 'Czy na pewno chcesz zakończyć ten czat? Rozmowa zostanie wyczyszczona.',
  };

  // Inactivity timer helpers
  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const resetInactivityTimer = () => {
    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(async () => {
      if (!leadSentRef.current && messagesRef.current.length > 0) {
        try {
          await maybeSendLead(messagesRef.current, 'inactivity');
          setLeadSent(true);
        } catch (err) {
          console.error('Failed to send inactivity lead:', err);
        }
      }
    }, INACTIVITY_MS);
  };

  // When chat ends, send conversation snapshot to backend
  const maybeSendLead = async (fullConversation, trigger = 'chat_closed') => {
    const { email } = extractContactInfo(fullConversation);

    try {
      const projectDescription = fullConversation
        .map((msg) => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
        .join('\n');

      const leadData = {
        name: 'Website visitor',
        // Backend requires an email; if only phone is provided,
        // send a placeholder and include the phone in the project.
        email: email || 'no-email-provided@websty.lead',
        project: projectDescription,
        language,
        sessionId: sessionManagerRef.current.getSessionId(),
        trigger,
      };

      console.log('Sending lead to /api/lead:', leadData);

      const response = await fetch(`${API_BASE_URL}/api/lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(leadData),
      });

      if (!response.ok) {
        throw new Error(`Lead API returned ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Lead sent successfully:', result);
    } catch (error) {
      console.error('Error sending lead:', error);
      // Re-throw to allow caller to handle if needed
      throw error;
    }
  };

  // Load saved conversation on mount
  useEffect(() => {
    const savedMessages = sessionManagerRef.current.loadConversation();
    if (savedMessages.length > 0) {
      // Filter out timestamps for display (keep only role and content)
      const displayMessages = savedMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      setMessages(displayMessages);
    }
  }, []);

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Add timestamps when saving
      const messagesWithTimestamps = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: Date.now()
      }));
      sessionManagerRef.current.saveConversation(messagesWithTimestamps);
    }
  }, [messages]);

  // Keep refs in sync so timer callbacks always see latest state
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { leadSentRef.current = leadSent; }, [leadSent]);

  // Send lead when user navigates away from page (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      clearInactivityTimer();
      // Only send if we have messages and haven't sent lead yet
      if (!leadSent && messages.length > 0) {
        const { email } = extractContactInfo(messages);
        const projectDescription = messages
          .map((msg) => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
          .join('\n');

        const leadData = {
          name: 'Website visitor',
          email: email || 'no-email-provided@websty.lead',
          project: projectDescription,
          language,
          sessionId: sessionManagerRef.current.getSessionId(),
          trigger: 'page_unload',
        };

        // Use sendBeacon for reliability during page unload (fires even if page is closing)
        // Note: sendBeacon doesn't support custom headers, but most backends accept JSON in body
        const blob = new Blob([JSON.stringify(leadData)], { type: 'application/json' });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(`${API_BASE_URL}/api/lead`, blob);
          console.log('Lead sent via sendBeacon on page unload');
        } else {
          // Fallback: try synchronous fetch (less reliable but better than nothing)
          fetch(`${API_BASE_URL}/api/lead`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(leadData),
            keepalive: true, // Keep request alive even after page unloads
          }).catch(err => console.error('Failed to send lead on unload:', err));
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [messages, leadSent, language]);

  useEffect(() => {
    // Send resize message to parent when widget opens/closes
    const height = isOpen ? WIDGET_CONFIG.WIDGET_HEIGHT : WIDGET_CONFIG.IFRAME_SIZE;
    const width = isOpen ? WIDGET_CONFIG.WIDGET_WIDTH : WIDGET_CONFIG.IFRAME_SIZE;
    if (window.parent && window.parent !== window) {
      // Send immediately
      window.parent.postMessage({
        type: 'chatbot-resize',
        width: width,
        height: height
      }, '*');
      
      // Also send after a small delay to ensure parent receives it
      setTimeout(() => {
        window.parent.postMessage({
          type: 'chatbot-resize',
          width: width,
          height: height
        }, '*');
      }, 100);
      
      // Send again after animation completes
      setTimeout(() => {
        window.parent.postMessage({
          type: 'chatbot-resize',
          width: width,
          height: height
        }, '*');
      }, 400);
    }
  }, [isOpen]);

  // Send initial size on mount
  useEffect(() => {
    if (window.parent && window.parent !== window) {
      // Send initial size after a short delay to ensure parent is ready
      setTimeout(() => {
        window.parent.postMessage({
          type: 'chatbot-resize',
          width: WIDGET_CONFIG.IFRAME_SIZE,
          height: WIDGET_CONFIG.IFRAME_SIZE
        }, '*');
      }, 200);
    }
  }, []);

  // Listen for size requests from parent
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data && e.data.type === 'request-size') {
        const height = isOpen ? WIDGET_CONFIG.WIDGET_HEIGHT : WIDGET_CONFIG.IFRAME_SIZE;
        const width = isOpen ? WIDGET_CONFIG.WIDGET_WIDTH : WIDGET_CONFIG.IFRAME_SIZE;
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({
            type: 'chatbot-resize',
            width: width,
            height: height
          }, '*');
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  // Handle language change - hide language menu after selection
  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    setShowLanguageMenu(false);
    setShowDropdown(false);
  };

  // Toggle dropdown menu
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleChangeLanguage = () => {
    setShowLanguageMenu(true);
    setShowDropdown(false);
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      // Call your Vercel chatbot API
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: updatedMessages,
          language,
          sessionId: sessionManagerRef.current.getSessionId(), // Include session ID
        }),
      });

      const data = await response.json();
      const aiMessage = { role: 'assistant', content: data.message };
      const finalMessages = [...updatedMessages, aiMessage];
      setMessages(finalMessages);
      resetInactivityTimer(); // Restart 15-min inactivity clock after each exchange
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = { role: 'assistant', content: 'Sorry, something went wrong.' };
      setMessages([...messages, userMessage, errorMessage]);
    }

    setLoading(false);
  };

  const handleEndChat = async () => {
    const confirmMessage = END_CHAT_CONFIRM_BY_LANG[language] || END_CHAT_CONFIRM_BY_LANG.en;
    if (window.confirm(confirmMessage)) {
      clearInactivityTimer();
      // Send lead once when user explicitly ends chat
      if (!leadSent && messages.length > 0) {
        try {
          await maybeSendLead(messages, 'chat_closed');
          setLeadSent(true);
        } catch (error) {
          console.error('Failed to send lead on end chat:', error);
        }
      }

      sessionManagerRef.current.clearConversation();
      setMessages([]);
      setIsOpen(false);
      setShowLanguageMenu(true); // Reset language menu for next time
      setShowDropdown(false);
      setLeadSent(false);
    }
  };

  const handleCloseWidget = async () => {
    clearInactivityTimer();
    // If user closes widget after a conversation and we haven't sent a lead yet, send it
    if (!leadSent && messages.length > 0) {
      try {
        await maybeSendLead(messages, 'chat_closed');
        setLeadSent(true);
      } catch (error) {
        console.error('Failed to send lead on close widget:', error);
      }
    }
    setIsOpen(false);
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <div className="chat-button" onClick={() => setIsOpen(true)}>
          💬
        </div>
      )}

      {/* Chat Widget */}
      {isOpen && (
        <div className="chat-widget">
          <div className="chat-header">
            <h3>{HEADER_BY_LANG[language] || HEADER_BY_LANG.en}</h3>
            
            {/* Show language switcher if menu is open, otherwise show dropdown */}
            {showLanguageMenu ? (
              <div className="lang-switcher" role="group" aria-label="Choose language">
                <button
                  className={`lang-btn ${language === 'lt' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('lt')}
                  title="Lietuvių"
                  aria-label="Lithuanian"
                >
                  <img src="/flags/lithuania.png" alt="Lithuanian flag" />
                </button>
                <button
                  className={`lang-btn ${language === 'ru' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('ru')}
                  title="Русский"
                  aria-label="Russian"
                >
                  <img src="/flags/russia.png" alt="Russian flag" />
                </button>
                <button
                  className={`lang-btn ${language === 'pl' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('pl')}
                  title="Polski"
                  aria-label="Polish"
                >
                  <img src="/flags/poland.png" alt="Polish flag" />
                </button>
                <button
                  className={`lang-btn ${language === 'en' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('en')}
                  title="English"
                  aria-label="English"
                >
                  <img src="/flags/great-britain.png" alt="English flag" />
                </button>
              </div>
            ) : (
              <div className="header-buttons">
                <div className="dropdown-container">
                  <button className="menu-btn" onClick={toggleDropdown} aria-label="Menu">
                    ⋮
                  </button>
                  {showDropdown && (
                    <div className="dropdown-menu">
                      <button onClick={handleEndChat} className="dropdown-item">
                        {END_CHAT_BY_LANG[language] || END_CHAT_BY_LANG.en}
                      </button>
                      <button onClick={handleChangeLanguage} className="dropdown-item">
                        {CHANGE_LANGUAGE_BY_LANG[language] || CHANGE_LANGUAGE_BY_LANG.en}
                      </button>
                    </div>
                  )}
                </div>
                <button className="close-btn" onClick={handleCloseWidget}>
                  ✕
                </button>
              </div>
            )}
            
            {/* Close button - visible when language menu is showing */}
            {showLanguageMenu && (
              <button className="close-btn" onClick={handleCloseWidget}>
                ✕
              </button>
            )}
          </div>

          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="welcome-message">
                {WELCOME_BY_LANG[language] || WELCOME_BY_LANG.en}
              </div>
            )}
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.role}`}>
                {msg.content}
              </div>
            ))}
            {loading && (
              <div className="message assistant typing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
          </div>

          <div className="chat-input">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type a message..."
            />
            <button onClick={sendMessage} disabled={loading} className="send-btn" aria-label="Send message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default App;